import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HistoryIcon from '@mui/icons-material/History';
import SearchIcon from '@mui/icons-material/Search';
import { Autocomplete, Avatar, Box, Chip, CircularProgress, InputAdornment, TextField, Typography } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import useSubscriberUsernameSearchT, { OnlyFansUserT } from '../../hooks/useSubscriberUsernameSearchT';

const STORAGE_KEY = 'recentOnlyFansSearches';
const MAX_RECENT_SEARCHES = 10;

type Props = {
    selectedUser: OnlyFansUserT.Response | null;
    setSelectedUser: (user: OnlyFansUserT.Response | null) => void;
    disallowedUsernames?: string[];
};

const OnlyFansUsernameInputT: React.FC<Props> = ({ selectedUser, setSelectedUser, disallowedUsernames = [] }) => {
    const [inputValue, setInputValue] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [recentSearches, setRecentSearches] = useState<OnlyFansUserT.Response[]>([]);
    const debounceTimer = useRef<NodeJS.Timeout | null>(null);
    const disallowedUsernamesRef = useRef<string[]>(disallowedUsernames);

    // Update ref when disallowedUsernames changes
    useEffect(() => {
        disallowedUsernamesRef.current = disallowedUsernames;
    }, [disallowedUsernames]);

    const { data: searchResults, isFetching } = useSubscriberUsernameSearchT(debouncedQuery);

    // Load recent searches from localStorage ONCE on mount
    useEffect(() => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                const parsed = JSON.parse(stored);
                setRecentSearches(parsed);
            }
        } catch (error) {
            console.error('Failed to load recent searches:', error);
        }
    }, []); // Empty dependency array - run only once

    // Save successful search to localStorage
    const saveToRecentSearches = useCallback((user: OnlyFansUserT.Response) => {
        // Don't save disallowed usernames
        if (disallowedUsernamesRef.current.includes(user.username.toLowerCase())) {
            return;
        }

        setRecentSearches(prev => {
            // Remove if already exists
            const filtered = prev.filter(item => item.id !== user.id);
            // Add to beginning
            const updated = [user, ...filtered].slice(0, MAX_RECENT_SEARCHES);

            try {
                localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
            } catch (error) {
                console.error('Failed to save recent searches:', error);
            }

            return updated;
        });
    }, []); // No dependencies needed since we use ref

    // Debounce search input
    useEffect(() => {
        // Don't debounce if user is selected (chip is shown)
        if (selectedUser) {
            return;
        }

        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }

        debounceTimer.current = setTimeout(() => {
            setDebouncedQuery(inputValue.trim());
        }, 500);

        return () => {
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
            }
        };
    }, [inputValue, selectedUser]);

    // Handle user selection
    const handleUserSelect = (user: OnlyFansUserT.Response | null) => {
        setSelectedUser(user);
        if (user) {
            saveToRecentSearches(user);
            setInputValue(''); // Clear input when user is selected
        }
    };

    // Filter function to remove disallowed usernames
    const filterDisallowedUsers = (users: OnlyFansUserT.Response[]) => {
        return users.filter(user => !disallowedUsernamesRef.current.includes(user.username.toLowerCase()));
    };

    // Build options list (search results + recent searches)
    const options: OnlyFansUserT.Response[] = React.useMemo(() => {
        const opts: OnlyFansUserT.Response[] = [];
        const seenIds = new Set<number>();

        // Add search results first (filtered)
        if (searchResults && Array.isArray(searchResults) && searchResults.length > 0) {
            const filteredResults = filterDisallowedUsers(searchResults);
            filteredResults.forEach(result => {
                opts.push(result);
                seenIds.add(result.id);
            });
        }

        // Add recent searches that aren't in search results (filtered)
        if (!debouncedQuery || (searchResults && searchResults.length === 0)) {
            const filteredRecent = filterDisallowedUsers(recentSearches);
            filteredRecent.forEach(recent => {
                if (!seenIds.has(recent.id)) {
                    opts.push(recent);
                }
            });
        }

        return opts;
    }, [searchResults, recentSearches, debouncedQuery]); // Removed filterDisallowedUsers from dependencies

    // Check if showing recent searches
    const showingRecentSearches = !debouncedQuery && recentSearches.length > 0;

    // Clear input when user deselects
    const handleClearSelection = () => {
        setSelectedUser(null);
        setInputValue('');
    };

    return (
        <Autocomplete
            value={selectedUser}
            onChange={(event, newValue) => {
                handleUserSelect(newValue);
            }}
            inputValue={selectedUser ? '' : inputValue}
            onInputChange={(event, newInputValue, reason) => {
                // Prevent input when user is selected
                if (selectedUser) {
                    return;
                }

                if (reason === 'input') {
                    setInputValue(newInputValue);
                }
                if (reason === 'clear') {
                    setInputValue('');
                }
            }}
            options={options}
            getOptionLabel={option => option?.username || ''}
            isOptionEqualToValue={(option, value) => {
                // Handle null/undefined values
                if (!value) return false;
                return option.id === value.id;
            }}
            loading={isFetching}
            filterOptions={x => x} // Disable built-in filtering since we handle it
            noOptionsText={isFetching ? 'Searching...' : debouncedQuery ? 'No users found' : 'Start typing to search'}
            renderInput={params => (
                <TextField
                    {...params}
                    label="OnlyFans Username"
                    aria-autocomplete="none"
                    placeholder={selectedUser ? '' : 'Search for a username...'}
                    helperText={
                        selectedUser
                            ? `Selected: @${selectedUser.username}`
                            : showingRecentSearches
                            ? 'Recent searches shown below'
                            : 'Type to search for users'
                    }
                    InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                            <>
                                <InputAdornment position="start">
                                    {showingRecentSearches && !selectedUser ? <HistoryIcon /> : <SearchIcon />}
                                </InputAdornment>
                                {selectedUser && (
                                    <Chip
                                        avatar={<Avatar src={selectedUser.avatarThumbs?.c50} />}
                                        label={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <span>@{selectedUser.username}</span>
                                                {selectedUser.isVerified && <CheckCircleIcon sx={{ fontSize: 14 }} />}
                                            </Box>
                                        }
                                        onDelete={handleClearSelection}
                                        sx={{ ml: 0.5, mr: 0.5 }}
                                    />
                                )}
                            </>
                        ),
                        endAdornment: (
                            <>
                                {isFetching && !selectedUser ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                        readOnly: !!selectedUser, // Make readonly when user is selected
                    }}
                />
            )}
            renderOption={(props, option, state) => {
                const isFromRecentSearch = !searchResults?.some(result => result.id === option.id);

                return (
                    <Box component="li" {...props} key={option.id}>
                        <Avatar src={option.avatarThumbs?.c50} alt={option.name} sx={{ width: 40, height: 40, mr: 2 }} />
                        <Box sx={{ flexGrow: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="body1">@{option.username}</Typography>
                                {option.isVerified && <CheckCircleIcon color="primary" sx={{ fontSize: 16 }} />}
                                {isFromRecentSearch && (
                                    <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontStyle: 'italic' }}>
                                        Recent
                                    </Typography>
                                )}
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                {option.name}
                            </Typography>
                        </Box>
                    </Box>
                );
            }}
        />
    );
};

export default OnlyFansUsernameInputT;
